import { acceptHMRUpdate, defineStore } from "pinia";
import { type Ref, computed, ref } from "vue";

import { useIdentityStore } from "@/data/stores/identity";
import { useTableAllocationStore } from "@/data/stores/tableAllocation";

import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import useAppSocket from "@/data/dao/socket/composables/appSocket";
import type { ISelectedItem } from "@/data/model/Bill";
import { Bill, type IBill, type IUserItemSelection, type PaidLineItem, type UnpaidLineItem } from "@/data/model/Bill";
import { exceptionTypes } from "@/data/util/Config";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";

import { Popup } from "@/components/dialog/Popup";

import { trans } from "@/operator/localization/Trans";

export const useBillStore = defineStore("bill", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const identity = useIdentityStore();
    const allocation = useTableAllocationStore();

    // state
    const isLoading = ref<boolean>(false);
    const isUpdating = ref<boolean>(false);
    const currentBill = ref<IBill | null>(null);
    const unpaidItems = ref<UnpaidLineItem[]>([]) as Ref<UnpaidLineItem[]>;
    const paidItems = ref<PaidLineItem[]>([]) as Ref<PaidLineItem[]>;

    // getters
    const totalItemsSelected = computed(() => {
        let totalItems = 0;
        for (const selectedItem of selectedUnpaidItems.value) {
            totalItems += selectedItem.selectedQuantity;
        }
        return totalItems;
    });

    const totalPayableAmount = computed(() => getSelectedItemsTotalPayable(selectedUnpaidItems.value));
    const totalPaid = computed(() => currentBill.value?.totalPaid ?? 0);
    const discountAmount = computed(() => currentBill.value?.discountAmount ?? 0);

    const selectedUnpaidItems = computed(() => unpaidItems.value.filter((item) => item.selectedQuantity > 0));
    const hasUnpaidItems = computed(() => unpaidItems.value.length > 0);
    const hasPaidItems = computed(() => paidItems.value.length > 0);
    const isAnyItemSelected = computed(() => selectedUnpaidItems.value.length > 0);
    const isOpenBill = computed(() => currentBill.value != null);

    // collection of items to update on the server
    const selectedItemsList = computed(() => {
        const selectedItems = [] as ISelectedItem[];
        for (const selectedItem of selectedUnpaidItems.value) {
            selectedItems.push({
                lineItemId: selectedItem.id,
                quantity: selectedItem.selectedQuantity,
            });
        }

        return selectedItems;
    });

    // actions
    async function loadCurrentBill(): Promise<void> {
        isLoading.value = true;

        const currentOrderBillId = allocation.allocation?.orderBillId;
        try {
            const currentBill = await customerDao.getCurrentBill({ billId: currentOrderBillId });
            setCurrentBill(currentBill ?? null);
        } catch (ex) {
            logError(ex);
            const e = ex as { type: string };
            if (
                e.type === exceptionTypes.userNotAtTableException ||
                e.type === exceptionTypes.billDoesNotExistException
            ) {
                setCurrentBill(null);
            }

            throw e;
        } finally {
            isLoading.value = false;
        }
    }

    function setCurrentBill(newBill: IBill | null) {
        // todo: workaround for faster view refresh
        // there is a delay between cancelling items and closing bill
        // so there is a moment where there are no items to pay, but the bill is still not closed
        // here we simulate closed bill if there are no items to pay - because it will be closed within a few seconds
        if (newBill?.items.length == 0) {
            newBill = null;
        }

        if (newBill == null) {
            currentBill.value = null;
            unpaidItems.value = [];
            paidItems.value = [];
            return;
        }

        const identityId = identity.identityId ?? "";
        currentBill.value = newBill;
        unpaidItems.value = Bill.getUnpaidLineItems(newBill, identityId);
        paidItems.value = Bill.getPaidLineItems(newBill);
    }

    function getSelectedItemsTotalPayable(selectedItems: UnpaidLineItem[]) {
        let selectedItemsTotalPayableAmount = 0;
        for (const selectedItem of selectedItems) {
            selectedItemsTotalPayableAmount += selectedItem.selectedQuantity * selectedItem.priceWithOptions;
        }

        return selectedItemsTotalPayableAmount;
    }

    async function selectItem(item: UnpaidLineItem, quantity: number) {
        const unpaidItem = unpaidItems.value.find((x) => x.id === item.id);
        if (unpaidItem == null) return;

        unpaidItem.selectedQuantity = quantity;

        await sendSelectedItemsToServer();
    }

    async function selectAllItems() {
        for (const unpaidItem of unpaidItems.value) {
            unpaidItem.selectedQuantity = unpaidItem.unpaidQuantity;
        }

        await sendSelectedItemsToServer();
    }

    async function deselectAllItems() {
        for (const unpaidItem of unpaidItems.value) {
            unpaidItem.selectedQuantity = 0;
        }

        await sendSelectedItemsToServer();
    }

    function isForOrder(orderId: string) {
        for (const lineItem of currentBill.value?.items ?? []) {
            if (lineItem.orderId === orderId) {
                return true;
            }
        }

        return false;
    }

    async function sendSelectedItemsToServer(newSelection: ISelectedItem[] | null = null) {
        const selectionToSend = newSelection ?? selectedItemsList.value;

        if (!hasUnpaidItems.value) return;

        const billId = currentBill.value?.id;
        if (billId == null) return;

        try {
            isUpdating.value = true;

            const { appSocket } = useAppSocket();

            const selectItemsToPayResult = await appSocket.selectItemsToPay({
                selectedItems: selectionToSend,
                billId: billId,
            });

            if (!selectItemsToPayResult.isSuccess) {
                Popup.show({
                    message: translations.cart.changingTotalWillRemoveAppliedDiscountsMessage,
                    confirmHandler: async () => {
                        try {
                            await customerDao.removeAllBillDiscounts({ billId: billId });
                            sendSelectedItemsToServer(selectionToSend);
                            loadCurrentBill();
                        } catch (err) {
                            logError(err);
                            Popup.show({ message: trans.general.error });
                        }
                    },
                    confirmButton: translations.cart.removeDiscounts,
                    cancelButton: translations.general.cancel,
                    uniqueId: "changingTotalWillRemoveAppliedDiscountsPopup",
                    highlightConfirm: false,
                    highlightCancel: true,
                    cancelHandler: () => {
                        loadCurrentBill();
                    },
                });
            }
        } finally {
            isUpdating.value = false;
        }
    }

    function updateBillName(billId: string, name: string) {
        const currentBillId = currentBill.value?.id;
        if (currentBill.value && currentBillId != billId) {
            return;
        }

        currentBill.value!.customName = name;
    }

    function updateBillManualEndUsersCount(billId: string, manualEndUsersCount: number) {
        const currentBillId = currentBill.value?.id;
        if (currentBill.value && currentBillId != billId) {
            return;
        }

        currentBill.value!.manualEndUsersCount = manualEndUsersCount;
    }

    // event handlers
    function updateSelectedItems(selections: IUserItemSelection[]) {
        // update selected items quantity
        for (const selection of selections) {
            const unpaidItem = unpaidItems.value.find((item) => item.id === selection.lineItemId);
            if (unpaidItem == null) continue;

            unpaidItem.selectedQuantity = selection.quantity;
        }

        // unselect not selected items
        for (const existingSelections of selectedUnpaidItems.value) {
            const unpaidItem = selections.find((item) => item.lineItemId === existingSelections.id);
            if (unpaidItem == null) {
                existingSelections.selectedQuantity = 0;
            }
        }
    }

    return {
        // state
        isLoading,
        isUpdating,
        unpaidItems,
        paidItems,
        currentBill,

        // getters
        totalPayableAmount,
        totalItemsSelected,
        totalPaid,
        hasUnpaidItems,
        hasPaidItems,
        isAnyItemSelected,
        selectedItemsList,
        isOpenBill,
        discountAmount,

        // actions
        loadCurrentBill,
        setCurrentBill,
        selectItem,
        selectAllItems,
        deselectAllItems,
        updateSelectedItems,
        isForOrder,
        updateBillName,
        updateBillManualEndUsersCount,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useBillStore, import.meta.hot));
}
